import type { RefTextAreaType } from '@arco-design/web-react/es/Input/textarea';
import type { TextAreaProps } from '../const';
import { Input as ArcoInput } from '@arco-design/web-react';
import { cn } from '@utils';

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './index.less';

function TextArea(props: TextAreaProps, ref: React.Ref<RefTextAreaType | null>) {
  const {
    error,
    onBlur,
    onFocus,
    hideFocusStyle = false,
    className,
    wrapperClassName,
    isFocus: isFocusProp,
    ...restProps
  } = props;

  const [isFocus, setIsFocus] = useState(false);
  const $textarea = useRef<RefTextAreaType>(null);
  const $wrap = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => {
      return $textarea.current || null;
    },
    [],
  );

  useEffect(() => {
    const dom = $textarea.current?.dom;
    const wrap = $wrap.current;
    if (!dom || !wrap) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === dom) {
          const computedStyle = window.getComputedStyle(dom);
          const height = computedStyle.getPropertyValue('height');
          const minHeight = computedStyle.getPropertyValue('min-height');
          const maxHeight = computedStyle.getPropertyValue('max-height');
          wrap.style.height = height;
          wrap.style.minHeight = minHeight;
          wrap.style.maxHeight = maxHeight;
        }
      }
    });

    resizeObserver.observe(dom);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const textareaElement = $textarea.current;

    if (!isFocusProp || !$textarea || !textareaElement || isFocus) {
      return;
    }

    textareaElement.focus();
    setIsFocus(true);
  }, [isFocus, isFocusProp]);

  return (
    <div
      ref={$wrap}
      className={cn(
        'wg-textarea',
        {
          'wg-textarea--normal-focus': isFocus && !error && !hideFocusStyle,
          'wg-textarea--focus-error': isFocus && error,
          'wg-textarea--error': error && !isFocus,
        },
        wrapperClassName,
      )}
    >
      <ArcoInput.TextArea
        ref={$textarea}
        className={className}
        error={error}
        onFocus={(e) => {
          setIsFocus(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocus(false);
          onBlur?.(e);
        }}
        data-vaul-no-drag
        {...restProps}
      />
    </div>
  );
}

const TextAreaComponent = forwardRef<RefTextAreaType | null, TextAreaProps>(TextArea);

TextAreaComponent.displayName = 'TextArea';

export default TextAreaComponent;
